import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get, post, patch, del } from "../../lib/request"

function normalizeCategory(row) {
  if (!row || typeof row !== "object") return null
  if (row.id == null) return null
  return {
    id: row.id,
    clientId: row.clientId,
    description: String(row.description ?? ""),
  }
}

function normalizeList(raw) {
  if (!Array.isArray(raw)) return []
  return raw.map(normalizeCategory).filter(Boolean)
}

export const fetchAlertClientCategories = createAsyncThunk(
  "alertClientCategories/fetch",
  async (clientId, { rejectWithValue }) => {
    try {
      const data = await get(`alerts/clients/${clientId}/category`)
      return { clientId, categories: normalizeList(data) }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch alert categories")
    }
  },
)

export const createAlertClientCategory = createAsyncThunk(
  "alertClientCategories/create",
  async ({ clientId, description }, { dispatch, rejectWithValue }) => {
    try {
      const trimmed = String(description).trim()
      const query = new URLSearchParams({ description: trimmed })
      const created = await post(`alerts/clients/${clientId}/category?${query}`, undefined, { body: "" })
      const { categories } = await dispatch(fetchAlertClientCategories(clientId)).unwrap()
      const fromResponse = normalizeCategory(created)
      const fromList =
        categories.find((c) => String(c.description).trim() === trimmed) ??
        categories
          .filter((c) => String(c.description).trim() === trimmed)
          .sort((a, b) => Number(b.id) - Number(a.id))[0]
      const category = fromResponse ?? fromList ?? null
      return { clientId, category }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to create alert category")
    }
  },
)

export const updateAlertClientCategory = createAsyncThunk(
  "alertClientCategories/update",
  async ({ categoryId, description, clientId }, { dispatch, rejectWithValue }) => {
    try {
      const trimmed = String(description).trim()
      const query = new URLSearchParams({ description: trimmed })
      await patch(`alerts/category/${categoryId}?${query}`, undefined, { body: "" })
      const { categories } = await dispatch(fetchAlertClientCategories(clientId)).unwrap()
      const category =
        categories.find((c) => Number(c.id) === Number(categoryId)) ?? null
      return { clientId, categoryId, category, description: trimmed }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update alert category")
    }
  },
)

export const deleteAlertClientCategory = createAsyncThunk(
  "alertClientCategories/delete",
  async ({ categoryId, clientId }, { dispatch, rejectWithValue }) => {
    try {
      await del(`alerts/category/${categoryId}`)
      await dispatch(fetchAlertClientCategories(clientId)).unwrap()
      return { clientId, categoryId }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to delete alert category")
    }
  },
)

const alertClientCategoriesSlice = createSlice({
  name: "alertClientCategories",
  initialState: {
    byClientId: {},
    status: "idle",
    mutateStatus: "idle",
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAlertClientCategories.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(fetchAlertClientCategories.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.byClientId[action.payload.clientId] = action.payload.categories
      })
      .addCase(fetchAlertClientCategories.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })
      .addCase(createAlertClientCategory.pending, (state) => {
        state.mutateStatus = "loading"
      })
      .addCase(createAlertClientCategory.fulfilled, (state) => {
        state.mutateStatus = "succeeded"
      })
      .addCase(createAlertClientCategory.rejected, (state, action) => {
        state.mutateStatus = "failed"
        state.error = action.payload
      })
      .addCase(updateAlertClientCategory.pending, (state) => {
        state.mutateStatus = "loading"
      })
      .addCase(updateAlertClientCategory.fulfilled, (state) => {
        state.mutateStatus = "succeeded"
      })
      .addCase(updateAlertClientCategory.rejected, (state, action) => {
        state.mutateStatus = "failed"
        state.error = action.payload
      })
      .addCase(deleteAlertClientCategory.pending, (state) => {
        state.mutateStatus = "loading"
      })
      .addCase(deleteAlertClientCategory.fulfilled, (state) => {
        state.mutateStatus = "succeeded"
      })
      .addCase(deleteAlertClientCategory.rejected, (state, action) => {
        state.mutateStatus = "failed"
        state.error = action.payload
      })
  },
})

export default alertClientCategoriesSlice.reducer
